import { useState, useEffect } from 'react'
import { schoolService } from '@/services/schoolService'
import axios from 'axios'
import { getProxiedPath } from '@/lib/corsProxy'
import { API_URL } from '@/config/constants'
import { useTranslation } from 'react-i18next'

const API_BASE_URL = API_URL
const IS_DEV = import.meta.env.DEV

export function SubdomainDebug() {
  const [info, setInfo] = useState({
    hostname: '',
    isSubdomain: false,
    schoolName: '',
    fullUrl: '',
    apiBaseUrl: API_BASE_URL,
    authToken: 'Not available',
  })

  const [apiStatus, setApiStatus] = useState({
    publicEndpoint: 'Not tested',
    protectedEndpoint: 'Not tested',
    protectedEndpointWithAuth: 'Not tested',
  })

  const { t } = useTranslation('school')

  useEffect(() => {
    // Get information about the current URL
    const hostname = window.location.hostname
    const isSubdomain = schoolService.isSubdomainUrl()
    const schoolName = schoolService.getCurrentSchoolFromSubdomain() || 'none'
    const fullUrl = window.location.href

    setInfo({
      hostname,
      isSubdomain,
      schoolName,
      fullUrl,
      apiBaseUrl: API_BASE_URL,
      authToken: 'Using HTTP-only cookies',
    })

    // Test the API endpoints
    const testEndpoints = async () => {
      // Extract school name from hostname or URL
      let testSchoolName = ''
      if (hostname.includes('.localhost')) {
        testSchoolName = hostname.split('.')[0]
      } else if (window.location.pathname.includes('/school/')) {
        testSchoolName = window.location.pathname.split('/school/')[1]
      } else {
        testSchoolName = 'test'
      }

      // Test public endpoint
      try {
        // Construct the endpoint URL
        const publicEndpointPath = `/public/etablissement/url/${testSchoolName}`

        // Use the proxy in development mode
        const publicRequestUrl = IS_DEV
          ? getProxiedPath(publicEndpointPath, 'api')
          : `${API_BASE_URL}${publicEndpointPath}`

        console.log(t('schoolLandingPage.subdomainDebug.testing.publicEndpoint', { url: publicRequestUrl }))

        await axios.get(publicRequestUrl, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
        setApiStatus((prev) => ({ ...prev, publicEndpoint: t('schoolLandingPage.subdomainDebug.apiStatus.states.accessible') }))
      } catch (error: any) {
        // console.error(t('schoolLandingPage.subdomainDebug.errors.publicEndpoint', { error: error.message }))
        setApiStatus((prev) => ({
          ...prev,
          publicEndpoint: t('schoolLandingPage.subdomainDebug.apiStatus.states.error', { 
            status: error.response?.status || 'Unknown', 
            message: error.message 
          }),
        }))
      }

      // Test protected endpoint without auth
      try {
        // Construct the endpoint URL
        const protectedEndpointPath = `/etablissement/url/${testSchoolName}`

        // Use the proxy in development mode
        const protectedRequestUrl = IS_DEV
          ? getProxiedPath(protectedEndpointPath, 'api')
          : `${API_BASE_URL}${protectedEndpointPath}`

        console.log(t('schoolLandingPage.subdomainDebug.testing.protectedEndpoint', { url: protectedRequestUrl }))

        await axios.get(protectedRequestUrl, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
        setApiStatus((prev) => ({ ...prev, protectedEndpoint: t('schoolLandingPage.subdomainDebug.apiStatus.states.accessible') }))
      } catch (error: any) {
        // console.error(t('schoolLandingPage.subdomainDebug.errors.protectedEndpoint', { error: error.message }))
        setApiStatus((prev) => ({
          ...prev,
          protectedEndpoint: t('schoolLandingPage.subdomainDebug.apiStatus.states.error', { 
            status: error.response?.status || 'Unknown', 
            message: error.message 
          }),
        }))
      }

      // Test protected endpoint with auth (using HTTP-only cookies)
      try {
        // Construct the endpoint URL
        const authEndpointPath = `/etablissement/url/${testSchoolName}`

        // Use the proxy in development mode
        const authRequestUrl = IS_DEV
          ? getProxiedPath(authEndpointPath, 'api')
          : `${API_BASE_URL}${authEndpointPath}`

        console.log(t('schoolLandingPage.subdomainDebug.testing.protectedEndpointWithAuth', { url: authRequestUrl }))

        await axios.get(authRequestUrl, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        })
        setApiStatus((prev) => ({
          ...prev,
          protectedEndpointWithAuth: t('schoolLandingPage.subdomainDebug.apiStatus.states.accessible'),
        }))
      } catch (error: any) {
        // console.error(t('schoolLandingPage.subdomainDebug.errors.protectedEndpointWithAuth', { error: error.message }))
        setApiStatus((prev) => ({
          ...prev,
          protectedEndpointWithAuth: t('schoolLandingPage.subdomainDebug.apiStatus.states.error', { 
            status: error.response?.status || 'Unknown', 
            message: error.message 
          }),
        }))
      }
    }

    testEndpoints()
  }, [t])

  return (
    <div className="p-4 bg-gray-100 rounded-lg mb-4">
      <h2 className="text-lg font-bold mb-2">{t('schoolLandingPage.subdomainDebug.title')}</h2>
      <div className="space-y-1 text-sm font-mono">
        <p>
          <span className="font-bold">{t('schoolLandingPage.subdomainDebug.fields.hostname')}</span> {info.hostname}
        </p>
        <p>
          <span className="font-bold">{t('schoolLandingPage.subdomainDebug.fields.isSubdomain')}</span>{' '}
          {info.isSubdomain ? t('schoolLandingPage.subdomainDebug.values.yes') : t('schoolLandingPage.subdomainDebug.values.no')}
        </p>
        <p>
          <span className="font-bold">{t('schoolLandingPage.subdomainDebug.fields.schoolName')}</span>{' '}
          {info.schoolName === 'none' ? t('schoolLandingPage.subdomainDebug.values.none') : info.schoolName}
        </p>
        <p>
          <span className="font-bold">{t('schoolLandingPage.subdomainDebug.fields.fullUrl')}</span> {info.fullUrl}
        </p>
        <p>
          <span className="font-bold">{t('schoolLandingPage.subdomainDebug.fields.apiBaseUrl')}</span> {info.apiBaseUrl}
        </p>
        <p>
          <span className="font-bold">{t('schoolLandingPage.subdomainDebug.fields.authToken')}</span>{' '}
          {info.authToken === 'Available' ? t('schoolLandingPage.subdomainDebug.values.available') : t('schoolLandingPage.subdomainDebug.values.notAvailable')}
        </p>
        <div className="mt-2 pt-2 border-t border-gray-300">
          <p className="font-bold">{t('schoolLandingPage.subdomainDebug.apiStatus.title')}</p>
          <p>
            <span className="font-bold">{t('schoolLandingPage.subdomainDebug.apiStatus.publicEndpoint')}</span>{' '}
            {apiStatus.publicEndpoint === 'Not tested' ? t('schoolLandingPage.subdomainDebug.apiStatus.states.notTested') : apiStatus.publicEndpoint}
          </p>
          <p>
            <span className="font-bold">{t('schoolLandingPage.subdomainDebug.apiStatus.protectedEndpoint')}</span>{' '}
            {apiStatus.protectedEndpoint === 'Not tested' ? t('schoolLandingPage.subdomainDebug.apiStatus.states.notTested') : apiStatus.protectedEndpoint}
          </p>
          <p>
            <span className="font-bold">{t('schoolLandingPage.subdomainDebug.apiStatus.protectedEndpointWithAuth')}</span>{' '}
            {apiStatus.protectedEndpointWithAuth === 'Not tested' ? t('schoolLandingPage.subdomainDebug.apiStatus.states.notTested') : apiStatus.protectedEndpointWithAuth}
          </p>
        </div>
      </div>
    </div>
  )
}
